// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react'
import { observer } from 'mobx-react'
import { Button, Form, Message } from 'semantic-ui-react'

import { store } from 'services/state'
import {
  customerIdFromApiToken,
  DEFAULT_WIZARD_OPTIONS,
  funderIdFromApiToken,
  generatePremiumRequest,
  loadPersistedCustomerIds,
  persistCustomerIds,
  wizardEnumOptions
} from 'services/premium-quote-payload'
import { generateLixiPremiumPackage } from 'services/lixi-premium-quote-payload'
import {
  applyJsonToLixiPremiumTryItOut,
  applyJsonToPremiumTryItOut
} from 'services/swagger-try-it-out'

/**
 * Guided PremiumQuote request builder shown after Authorize and before Default.
 * Instruction used: scope-discipline.instructions.md
 */

function initialFormState() {
  const persisted = loadPersistedCustomerIds(store.username)
  const customerId = customerIdFromApiToken(store.idToken)
  const funderId = funderIdFromApiToken(store.idToken)
  return Object.assign({}, DEFAULT_WIZARD_OPTIONS, persisted, {
    customerId: customerId,
    funderId: funderId != null ? funderId : '',
    originatorId: persisted.originatorId != null ? String(persisted.originatorId) : ''
  })
}

export default observer(class PremiumQuotePayloadWizard extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      form: initialFormState(),
      preview: '',
      errors: [],
      notice: '',
      noticePositive: false
    }
  }

  componentDidUpdate() {
    const nextCustomerId = customerIdFromApiToken(store.idToken)
    const nextFunderId = funderIdFromApiToken(store.idToken)
    const persisted = loadPersistedCustomerIds(store.username)
    const nextForm = Object.assign({}, this.state.form)
    let changed = false
    if (nextCustomerId != null && nextCustomerId !== this.state.form.customerId) {
      nextForm.customerId = nextCustomerId
      changed = true
    }
    if (nextFunderId != null && nextFunderId !== this.state.form.funderId) {
      nextForm.funderId = nextFunderId
      changed = true
    }
    if (!this.state.form.originatorId && persisted.originatorId != null) {
      nextForm.originatorId = String(persisted.originatorId)
      changed = true
    }
    if (changed) this.setState({ form: nextForm })
  }

  handleChange = (event, data) => {
    const name = data.name
    const value = typeof data.checked === 'boolean' ? data.checked : data.value
    this.setState({
      form: Object.assign({}, this.state.form, { [name]: value }),
      notice: ''
    })
  }

  handleGenerate = () => {
    persistCustomerIds(store.username, {
      funderId: this.state.form.funderId,
      originatorId: this.state.form.originatorId
    })
    const generate = this.props.variant === 'lixi' ? generateLixiPremiumPackage : generatePremiumRequest
    const result = generate(this.state.form)
    this.setState({
      preview: JSON.stringify(result.payload, null, 2),
      errors: result.errors,
      notice: result.errors.length ? '' : 'Payload generated.',
      noticePositive: result.errors.length === 0
    })
  }

  handleApply = () => {
    if (!this.state.preview) {
      this.setState({ notice: 'Generate a payload first.', noticePositive: false, errors: [] })
      return
    }
    if (this.state.errors.length) {
      this.setState({ notice: 'Fix the errors before applying to Try it out.', noticePositive: false })
      return
    }
    const apply = this.props.variant === 'lixi' ? applyJsonToLixiPremiumTryItOut : applyJsonToPremiumTryItOut
    const result = apply(this.state.preview)
    this.setState({
      notice: result.ok ? 'Applied to Try it out.' : result.reason,
      noticePositive: result.ok
    })
  }

  handleCopy = () => {
    if (!this.state.preview) {
      this.setState({ notice: 'Generate a payload first.', noticePositive: false, errors: [] })
      return
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(this.state.preview).then(() => {
        this.setState({ notice: 'Copied JSON to clipboard.', noticePositive: true })
      }).catch(() => {
        this.setState({ notice: 'Could not copy. Select the preview and copy it manually.', noticePositive: false })
      })
      return
    }
    this.setState({ notice: 'Could not copy. Select the preview and copy it manually.', noticePositive: false })
  }

  render() {
    const form = this.state.form
    const enums = wizardEnumOptions(store.api)
    // Access so this observer re-renders after API Authentication / catalog load.
    void store.username
    void store.idToken
    void store.api
    return (
      <details className="premium-quote-wizard" open>
        <summary className="premium-quote-wizard__header">
          <span className="premium-quote-wizard__title">
            {this.props.variant === 'lixi' ? 'LIXI Premium Quote payload' : 'Premium Quote payload'}
          </span>
          <small className="premium-quote-wizard__hint">Funder ID and Customer ID come from the API login and cannot be edited.</small>
        </summary>
        <Form size="small">
          <Form.Group>
            <Form.Select
              width={4}
              compact
              label="Product"
              name="productName"
              options={enums.productName}
              value={form.productName}
              onChange={this.handleChange}
            />
            <Form.Select
              width={4}
              compact
              label="Channel"
              name="loanSourceChannel"
              options={enums.loanSourceChannel}
              value={form.loanSourceChannel}
              onChange={this.handleChange}
            />
            <Form.Field width={8} className="premium-quote-wizard__checks">
              <label>Options</label>
              <div className="premium-quote-wizard__checks-row">
                <Form.Checkbox
                  label="Guarantor"
                  name="includeGuarantor"
                  checked={!!form.includeGuarantor}
                  onChange={this.handleChange}
                />
                <Form.Checkbox
                  label="Capitalise premium"
                  name="premiumToBeCapitalised"
                  checked={form.premiumToBeCapitalised !== false}
                  onChange={this.handleChange}
                />
              </div>
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Input
              width={4}
              label="Funder ID"
              name="funderId"
              value={form.funderId != null ? form.funderId : ''}
              readOnly
              placeholder="From API token"
              required
            />
            <Form.Input
              width={4}
              label="Originator ID"
              name="originatorId"
              value={form.originatorId}
              onChange={this.handleChange}
            />
            <Form.Input
              width={4}
              label="Customer ID"
              name="customerId"
              value={form.customerId != null ? form.customerId : ''}
              readOnly
              placeholder="From API token"
            />
          </Form.Group>
          <Form.Group>
            <Form.Select
              width={4}
              compact
              label="Borrower"
              name="borrowerType"
              options={enums.borrowerType}
              value={form.borrowerType}
              onChange={this.handleChange}
            />
            <Form.Select
              width={3}
              compact
              label="Loans"
              name="loanCount"
              options={[
                { key: '1', value: 1, text: '1' },
                { key: '2', value: 2, text: '2' }
              ]}
              value={form.loanCount}
              onChange={this.handleChange}
            />
            <Form.Select
              width={5}
              compact
              label="Purpose"
              name="loanPurpose"
              options={enums.loanPurpose}
              value={form.loanPurpose}
              onChange={this.handleChange}
            />
            <Form.Input
              width={4}
              label="Loan amount"
              name="loanAmount"
              type="number"
              value={form.loanAmount}
              onChange={this.handleChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              width={3}
              label="Term (months)"
              name="loanTermInMonths"
              type="number"
              value={form.loanTermInMonths}
              onChange={this.handleChange}
            />
            <Form.Select
              width={4}
              compact
              label="Occupancy"
              name="occupancyType"
              options={enums.occupancyType}
              value={form.occupancyType}
              onChange={this.handleChange}
            />
            <Form.Select
              width={3}
              compact
              label="Security"
              name="securityType"
              options={enums.securityType}
              value={form.securityType}
              onChange={this.handleChange}
            />
            <Form.Input
              width={3}
              label="Value"
              name="securityValue"
              type="number"
              value={form.securityValue}
              onChange={this.handleChange}
            />
            <Form.Select
              width={3}
              compact
              label="State"
              name="state"
              options={enums.state}
              value={form.state}
              onChange={this.handleChange}
            />
          </Form.Group>
          <div className="premium-quote-wizard__actions">
            <Button primary size="small" type="button" onClick={this.handleGenerate}>Generate</Button>
            <Button size="small" type="button" onClick={this.handleApply} disabled={!this.state.preview}>Apply to Try it out</Button>
            <Button size="small" type="button" onClick={this.handleCopy} disabled={!this.state.preview}>Copy JSON</Button>
          </div>
        </Form>
        {this.state.errors.length > 0 && (
          <Message negative size="tiny">
            {this.state.errors.map(function (err) { return <p key={err}>{err}</p> })}
          </Message>
        )}
        {this.state.notice && (
          <Message size="tiny" positive={this.state.noticePositive} negative={!this.state.noticePositive}>
            {this.state.notice}
          </Message>
        )}
        {this.state.preview && (
          <details className="premium-quote-wizard__preview">
            <summary>Generated JSON</summary>
            <pre>{this.state.preview}</pre>
          </details>
        )}
      </details>
    )
  }
})
